'use client';

import { Suspense, useState } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { createClient } from '@/lib/supabase/client';
import { z } from 'zod';

function SignupForm() {
  const { t } = useTranslation();
  const searchParams = useSearchParams();

  const signupSchema = z.object({
    name: z.string().min(2, t('auth.validationName')),
    email: z.string().email(t('auth.validationEmail')),
    password: z.string().min(8, t('auth.validationPasswordLength')),
  });
  const plan = searchParams.get('plan');
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ name?: string; email?: string; password?: string }>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    setSuccessMessage(null);

    // Validate form
    const result = signupSchema.safeParse({ name, email, password });
    if (!result.success) {
      const errors: { name?: string; email?: string; password?: string } = {};
      result.error.issues.forEach((err) => {
        if (err.path[0] === 'name') errors.name = err.message;
        if (err.path[0] === 'email') errors.email = err.message;
        if (err.path[0] === 'password') errors.password = err.message;
      });
      setFieldErrors(errors);
      return;
    }

    setIsLoading(true);

    try {
      const supabase = createClient();
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
            plan: plan || 'free',
          },
          emailRedirectTo: `${window.location.origin}/api/auth/callback`,
        },
      });

      if (error) {
        setError(error.message);
        return;
      }

      setSuccessMessage(t('auth.checkEmail'));
    } catch {
      setError(t('auth.unexpectedError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setIsGoogleLoading(true);
    setError(null);

    try {
      const supabase = createClient();
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/api/auth/callback${plan ? `?plan=${plan}` : ''}`,
        },
      });

      if (error) {
        setError(error.message);
        setIsGoogleLoading(false);
      }
    } catch {
      setError(t('auth.unexpectedError'));
      setIsGoogleLoading(false);
    }
  };

  // Password strength indicator
  const getPasswordStrength = (pwd: string) => {
    let strength = 0;
    if (pwd.length >= 8) strength++;
    if (/[A-Z]/.test(pwd)) strength++;
    if (/[a-z]/.test(pwd)) strength++;
    if (/[0-9]/.test(pwd)) strength++;
    if (/[^A-Za-z0-9]/.test(pwd)) strength++;
    return strength;
  };

  const passwordStrength = getPasswordStrength(password);
  const strengthColors = ['bg-red-500', 'bg-red-500', 'bg-yellow-500', 'bg-yellow-500', 'bg-green-500', 'bg-green-500'];
  const strengthTexts = ['', 'Weak', 'Fair', 'Good', 'Strong', 'Excellent'];

  return (
    <div className="space-y-8">
      {/* Mobile Logo */}
      <div className="lg:hidden flex justify-center mb-8">
        <Link href="/" className="flex items-center gap-3">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/logo.png"
            alt="BrainEasy"
            className="w-10 h-10"
          />
          <span className="text-xl font-bold text-white tracking-tight">BrainEasy</span>
        </Link>
      </div>

      {/* Plan Badge */}
      {plan && (
        <div className="bg-primary/10 border border-primary/20 rounded-xl px-4 py-3 flex items-center gap-3">
          <span className="material-symbols-outlined text-primary">verified</span>
          <div>
            <p className="text-white text-sm font-medium">
              {t('auth.signingUpForPlan', { plan })}
            </p>
            <p className="text-gray-400 text-xs">{t('auth.changeAnytime')}</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="text-center lg:text-left">
        <h2 className="text-3xl font-black text-white tracking-tight">{t('auth.createAccount')}</h2>
        <p className="text-gray-400 mt-2">
          {t('auth.alreadyHaveAccount')}{' '}
          <Link href="/login" className="text-primary font-semibold hover:underline">
            {t('auth.signIn')}
          </Link>
        </p>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-xl px-4 py-4 flex items-start gap-3">
          <span className="material-symbols-outlined text-green-400 text-lg mt-0.5">check_circle</span>
          <div>
            <p className="text-green-400 font-medium">{successMessage}</p>
            <p className="text-green-400/70 text-sm mt-1">
              {t('auth.clickLinkToComplete')}
            </p>
          </div>
        </div>
      )}

      {/* Form */}
      {!successMessage && (
        <>
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Global Error */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 flex items-start gap-3">
                <span className="material-symbols-outlined text-red-400 text-lg mt-0.5">error</span>
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            {/* Name Field */}
            <div className="space-y-2">
              <label htmlFor="name" className="block text-sm font-medium text-gray-300">
                {t('auth.fullName')}
              </label>
              <div className="relative">
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  className={`w-full bg-white/5 border ${
                    fieldErrors.name ? 'border-red-500' : 'border-white/10'
                  } rounded-xl px-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors`}
                  disabled={isLoading}
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-gray-500 text-lg">
                  person
                </span>
              </div>
              {fieldErrors.name && (
                <p className="text-red-400 text-sm flex items-center gap-1">
                  <span className="material-symbols-outlined text-sm">error</span>
                  {fieldErrors.name}
                </p>
              )}
            </div>

            {/* Email Field */}
            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                {t('auth.email')}
              </label>
              <div className="relative">
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className={`w-full bg-white/5 border ${
                    fieldErrors.email ? 'border-red-500' : 'border-white/10'
                  } rounded-xl px-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors`}
                  disabled={isLoading}
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-gray-500 text-lg">
                  mail
                </span>
              </div>
              {fieldErrors.email && (
                <p className="text-red-400 text-sm flex items-center gap-1">
                  <span className="material-symbols-outlined text-sm">error</span>
                  {fieldErrors.email}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                {t('auth.password')}
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className={`w-full bg-white/5 border ${
                    fieldErrors.password ? 'border-red-500' : 'border-white/10'
                  } rounded-xl px-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors pr-12`}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                >
                  <span className="material-symbols-outlined text-lg">
                    {showPassword ? 'visibility_off' : 'visibility'}
                  </span>
                </button>
              </div>
              {fieldErrors.password && (
                <p className="text-red-400 text-sm flex items-center gap-1">
                  <span className="material-symbols-outlined text-sm">error</span>
                  {fieldErrors.password}
                </p>
              )}
              
              {/* Password Strength Indicator */}
              {password && (
                <div className="space-y-2">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((level) => (
                      <div
                        key={level}
                        className={`h-1 flex-1 rounded-full transition-colors ${
                          level <= passwordStrength ? strengthColors[passwordStrength] : 'bg-white/10'
                        }`}
                      />
                    ))}
                  </div>
                  <p className={`text-xs ${strengthColors[passwordStrength].replace('bg-', 'text-').replace('-500', '-400')}`}>
                    {strengthTexts[passwordStrength]}
                  </p>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary text-white font-bold py-3.5 rounded-xl shadow-[0_4px_0_#CC5500] hover:brightness-110 active:shadow-none active:translate-y-1 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:active:translate-y-0 disabled:active:shadow-[0_4px_0_#CC5500] flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <span className="material-symbols-outlined animate-spin text-lg">progress_activity</span>
                  {t('auth.creatingAccount')}
                </>
              ) : (
                t('auth.createAccountButton')
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-black text-gray-500">{t('auth.orContinueWith')}</span>
            </div>
          </div>

          {/* Social Login */}
          <button
            onClick={handleGoogleSignUp}
            disabled={isGoogleLoading || isLoading}
            className="w-full bg-white/5 border border-white/10 text-white font-medium py-3.5 rounded-xl hover:bg-white/10 hover:border-white/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
          >
            {isGoogleLoading ? (
              <>
                <span className="material-symbols-outlined animate-spin text-lg">progress_activity</span>
                {t('auth.connecting')}
              </>
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {t('auth.continueWithGoogle')}
              </>
            )}
          </button>

          {/* Terms */}
          <p className="text-center text-xs text-gray-500">
            {t('auth.termsAgreeSignup')}{' '}
            <Link href="/terms" className="text-gray-400 hover:text-white underline">
              {t('auth.termsOfService')}
            </Link>{' '}
            and{' '}
            <Link href="/privacy" className="text-gray-400 hover:text-white underline">
              {t('auth.privacyPolicy')}
            </Link>
          </p>
        </>
      )}
    </div>
  );
}

// Loading fallback for the signup form
function SignupFormSkeleton() {
  return (
    <div className="space-y-8 animate-pulse">
      <div className="lg:hidden flex justify-center mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/10 rounded-lg" />
          <div className="w-24 h-6 bg-white/10 rounded" />
        </div>
      </div>
      <div className="space-y-2">
        <div className="w-40 h-8 bg-white/10 rounded" />
        <div className="w-48 h-4 bg-white/10 rounded" />
      </div>
      <div className="space-y-5">
        {[1, 2, 3].map((i) => (
          <div key={i} className="space-y-2">
            <div className="w-20 h-4 bg-white/10 rounded" />
            <div className="w-full h-12 bg-white/5 border border-white/10 rounded-xl" />
          </div>
        ))}
        <div className="w-full h-12 bg-primary/20 rounded-xl" />
      </div>
    </div>
  );
}

export default function SignupPage() {
  return (
    <Suspense fallback={<SignupFormSkeleton />}>
      <SignupForm />
    </Suspense>
  );
}
